import { Link } from 'react-router-dom'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import SpeczeLogo from '../assets/SpeczeLogo'

// ── Floating orb decoration ──────────────────────────────────────────────
function Orb({ className }) {
  return <div className={`absolute rounded-full blur-3xl pointer-events-none ${className}`} />
}

// ── Provider logos ───────────────────────────────────────────────────────
function GithubIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
    </svg>
  )
}
function GitlabIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
      <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 01-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 014.82 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0118.6 2a.43.43 0 01.58 0 .42.42 0 01.11.18l2.44 7.51L23 13.45a.84.84 0 01-.35.94z" fill="#FC6D26"/>
    </svg>
  )
}
function BitbucketIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
      <path d="M.778 1.213a.768.768 0 00-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 00.77-.646l3.27-20.03a.768.768 0 00-.768-.891L.778 1.213zM14.52 15.53H9.522L8.17 8.466h7.561l-1.211 7.064z" fill="#2684FF"/>
    </svg>
  )
}

// ── Feature card ──────────────────────────────────────────────────────────
function FeatureCard({ icon, title, description, delay = 0 }) {
  return (
    <div
      className="group glass-card-hover overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Illustration area */}
      <div className="relative h-44 flex items-center justify-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(30,18,87,0.8) 0%, rgba(60,20,120,0.6) 100%)' }}>
        {/* Stars */}
        <div className="absolute inset-0 stars-bg opacity-60" />
        {/* Purple cloud shapes */}
        <div className="absolute bottom-0 left-0 right-0 h-16"
          style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(139,63,255,0.3) 0%, transparent 70%)' }} />
        <div className="relative z-10 text-5xl filter drop-shadow-lg transform group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
      </div>
      {/* Text */}
      <div className="p-6 bg-white/[0.02]">
        <h3 className="font-display font-bold text-white text-lg mb-2">{title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

// ── How it works step ─────────────────────────────────────────────────────
function Step({ number, title, description, icon }) {
  return (
    <div className="flex gap-5 items-start">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-display font-bold text-sm text-white"
        style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
        {number}
      </div>
      <div>
        <h3 className="font-semibold text-white mb-1">{title}</h3>
        <p className="text-sm text-white/50 leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

// ── Pricing card ─────────────────────────────────────────────────────────
function PricingCard({ plan, price, features, cta, highlight }) {
  return (
    <div className={`glass-card p-8 flex flex-col relative overflow-hidden transition-all duration-300 hover:-translate-y-1 ${highlight ? 'border-purple-500/40 shadow-specze' : ''}`}>
      {highlight && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400" />
      )}
      {highlight && (
        <span className="absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full"
          style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff' }}>
          Popular
        </span>
      )}
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-widest text-purple-400 mb-2">{plan}</p>
        <div className="flex items-end gap-1">
          <span className="text-4xl font-display font-bold text-white">{price}</span>
          {price !== 'Free' && <span className="text-white/40 text-sm mb-1">/month</span>}
        </div>
      </div>
      <ul className="space-y-3 flex-1 mb-8">
        {features.map(f => (
          <li key={f} className="flex items-center gap-2.5 text-sm text-white/70">
            <svg className="w-4 h-4 text-purple-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
            </svg>
            {f}
          </li>
        ))}
      </ul>
      <Link to="/register"
        className={`text-center py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
          highlight
            ? 'btn-specze text-white block'
            : 'border border-white/20 text-white hover:border-purple-500/50 hover:bg-white/5 block'
        }`}>
        {cta}
      </Link>
    </div>
  )
}

export default function Landing() {
  return (
    <div className="relative overflow-hidden">
      <Navbar />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center pt-16 overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #07020f 0%, #180a3a 30%, #0d1a5c 60%, #1a0533 80%, #07020f 100%)' }}>

        {/* Stars */}
        <div className="absolute inset-0 stars-bg opacity-70" />

        {/* Orbs */}
        <Orb className="w-[600px] h-[600px] top-[-200px] left-1/2 -translate-x-1/2 bg-purple-600/20" />
        <Orb className="w-[400px] h-[400px] bottom-[100px] left-[-100px] bg-blue-600/15" />
        <Orb className="w-[300px] h-[300px] bottom-[50px] right-[-50px] bg-pink-600/15" />

        {/* Cloud silhouettes left */}
        <div className="absolute left-0 bottom-0 w-64 h-64 pointer-events-none opacity-70"
          style={{ background: 'radial-gradient(ellipse at 0% 100%, rgba(80,20,160,0.5) 0%, transparent 70%)' }} />
        <div className="absolute right-0 bottom-0 w-64 h-64 pointer-events-none opacity-70"
          style={{ background: 'radial-gradient(ellipse at 100% 100%, rgba(150,40,200,0.4) 0%, transparent 70%)' }} />

        <div className="relative z-10 max-w-4xl mx-auto px-6 py-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-500/30 bg-purple-500/10 text-xs font-semibold text-purple-300 mb-8">
            <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
            Now supporting GitHub · GitLab · Bitbucket
          </div>

          {/* Heading */}
          <h1 className="font-display font-bold leading-[1.1] mb-6" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}>
            <span className="text-white">Turn Your </span>
            <span className="gradient-text">Git Repositories</span>
            <span className="text-white"> Into<br />Specifications, Docs & Test Plans</span>
          </h1>

          <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed mb-10">
            Automatically generate structured documentation, test plans, and business specifications from your source code.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-16">
            <Link to="/register" className="btn-specze-lg text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
              Start Free Trial
            </Link>
            <a href="#demo" className="btn-outline-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              View Demo
            </a>
          </div>

          {/* Hero illustration — flow diagram */}
          <div className="relative flex items-center justify-center gap-6 md:gap-10">
            {/* Left: Code/Repo */}
            <div className="glass-card p-5 rounded-2xl flex-shrink-0 animate-float" style={{ animationDelay: '0s' }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-2"
                style={{ background: 'linear-gradient(135deg, #ef4444, #f97316)' }}>
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                </svg>
              </div>
              <p className="text-xs text-white/50 text-center font-mono">source code</p>
            </div>

            {/* Arrow */}
            <svg className="w-12 h-4 text-purple-400 hidden md:block" fill="none" viewBox="0 0 48 16">
              <path d="M0 8h44M38 2l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>

            {/* Center: Specze */}
            <div className="relative animate-float" style={{ animationDelay: '2s' }}>
              <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-specze-lg"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed, #a855f7)' }}>
                <SpeczeLogo size={48} showText={false} />
              </div>
              <div className="absolute inset-0 rounded-full bg-purple-500/20 blur-xl" />
            </div>

            {/* Arrow */}
            <svg className="w-12 h-4 text-purple-400 hidden md:block" fill="none" viewBox="0 0 48 16">
              <path d="M0 8h44M38 2l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>

            {/* Right: Document */}
            <div className="glass-card p-5 rounded-2xl flex-shrink-0 animate-float" style={{ animationDelay: '1s' }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-2"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <p className="text-xs text-white/50 text-center font-mono">docs & specs</p>
            </div>
          </div>
        </div>

        {/* Provider strip */}
        <div className="relative z-10 w-full border-t border-white/8 bg-cosmic-950/50 backdrop-blur py-6">
          <p className="text-xs text-white/30 text-center mb-4 uppercase tracking-widest font-semibold">
            Generated Platform modules
          </p>
          <div className="flex items-center justify-center gap-10">
            <div className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
              <GithubIcon />
              <span className="font-semibold">GitHub</span>
            </div>
            <div className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
              <GitlabIcon />
              <span className="font-semibold">GitLab</span>
            </div>
            <div className="flex items-center gap-2.5 text-white/70 hover:text-white transition-colors">
              <BitbucketIcon />
              <span className="font-semibold">Bitbucket</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────── */}
      <section id="features" className="py-24 relative"
        style={{ background: 'linear-gradient(180deg, #07020f 0%, #0d0521 50%, #07020f 100%)' }}>
        <div className="absolute inset-0 stars-bg opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-4xl text-white mb-4">
              From <span className="gradient-text">Code</span> to Complete Project Documentation
            </h2>
            <p className="text-white/50 max-w-xl mx-auto">
              One platform to generate every document your team needs — automatically.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon="⚙️"
              title="Automated Specs"
              description="Generate structured specifications directly from your repository — functional, technical, and API specs in minutes."
              delay={0}
            />
            <FeatureCard
              icon="📄"
              title="Documentation Engine"
              description="Produce comprehensive business documentation automatically — executive summaries, user journeys, architecture overviews."
              delay={100}
            />
            <FeatureCard
              icon="🧪"
              title="Test Plan Generator"
              description="Create structured QA test plans from system logic — unit, integration, E2E, and full coverage options."
              delay={200}
            />
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
      <section id="solutions" className="py-24"
        style={{ background: 'linear-gradient(180deg, #07020f 0%, #110830 100%)' }}>
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-purple-400 mb-3">How it works</p>
            <h2 className="font-display font-bold text-3xl text-white mb-10 leading-snug">
              Connect once, generate<br /><span className="gradient-text">everything instantly</span>
            </h2>
            <div className="space-y-8">
              <Step number="01" title="Connect your repository"
                description="Link your GitHub, GitLab, or Bitbucket repository in seconds with OAuth or a personal access token." />
              <Step number="02" title="Choose your output"
                description="Select the type of document — business spec, technical doc, test plan, API reference, or all of them." />
              <Step number="03" title="AI generates everything"
                description="Claude AI analyzes your codebase and produces publication-ready documents in minutes." />
              <Step number="04" title="Download & share"
                description="Export as Word, Markdown, or PDF. Share links with your team or clients instantly." />
            </div>
          </div>
          {/* Visual panel */}
          <div className="glass-card p-6 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(139,63,255,0.15) 0%, transparent 70%)' }} />
            <div className="relative space-y-3">
              {['Fetching repository files…', 'Analyzing code structure…', 'Generating specifications…', 'Building test scenarios…', 'Producing Word document…', '✓ Document ready!'].map((step, i) => (
                <div key={step} className="flex items-center gap-3 text-sm"
                  style={{ opacity: i === 5 ? 1 : 0.6 + i * 0.08 }}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${i === 5 ? 'bg-emerald-400' : 'bg-purple-400'}`} />
                  <span className={i === 5 ? 'text-emerald-400 font-semibold' : 'text-white/70 font-mono text-xs'}>{step}</span>
                </div>
              ))}
              <div className="mt-6 p-4 rounded-xl" style={{ background: 'rgba(16,24,40,0.8)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  <span className="font-mono text-xs text-white/30 ml-2">output.docx</span>
                </div>
                <div className="space-y-1.5">
                  {['# Executive Summary', '## System Architecture', '## User Journeys', '## API Endpoints'].map(line => (
                    <div key={line} className="font-mono text-[11px] text-purple-300 opacity-80">{line}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────────────── */}
      <section id="pricing" className="py-24"
        style={{ background: 'linear-gradient(180deg, #110830 0%, #07020f 100%)' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="font-display font-bold text-4xl text-white mb-4">Simple, transparent pricing</h2>
            <p className="text-white/50">Start free, scale as you grow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <PricingCard plan="Free" price="Free"
              features={['3 projects', '5 repositories', '10 documents/month', 'GitHub only', 'Email support']}
              cta="Get Started Free" />
            <PricingCard plan="Pro" price="$29" highlight
              features={['20 projects', '50 repositories', '100 documents/month', 'All providers', 'Test plans & specs', 'Priority support']}
              cta="Start Pro Trial" />
            <PricingCard plan="Enterprise" price="$99"
              features={['Unlimited projects', 'Unlimited repos', 'Unlimited documents', 'SSO & SAML', 'Dedicated support', 'SLA guarantee']}
              cta="Contact Sales" />
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ─────────────────────────────────────────────── */}
      <section className="py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1e1257 0%, #2a1a72 50%, #1e1257 100%)' }}>
        <Orb className="w-[500px] h-[500px] -top-40 left-1/2 -translate-x-1/2 bg-purple-600/25" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-bold text-4xl text-white mb-4">
            Ready to automate your documentation?
          </h2>
          <p className="text-white/60 mb-8 text-lg">
            Join thousands of developers who ship faster with Specze.
          </p>
          <Link to="/register" className="btn-specze-lg text-white inline-flex">
            Start For Free — No Card Required
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}
